import { useCallback, useState, useEffect } from 'react';
import type { FileRejection } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PapiDeviation, PapiAttachmentFile } from '@wix/da-papi-types';
import { getCurrentUserHasCore } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { useFileUpload } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import {
  pushGlobalConfirmationModal,
  pushModal,
} from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import type { UploadAttachmentSuccessCallback } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { uploadAttachment, removeAttachment } from '@wix/da-shared-react/pkg/redux/uploads/actions';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { getIsAttachmentDeviationLocked } from '@wix/da-shared-react/pkg/redux/uploads/selectors';
import { UploadType } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { useMaxAttachmentsAllowed } from './useMaxAttachmentsAllowed';
import {
  getAdditionalAttachmentAcceptedMimeTypes,
  getAdditionalAttachmentType,
} from '../utils/additionalMediaUpload';
import { useUnlockVideoUploadsCheck } from '../modals/UnlockWithCoreUpsellModal/useUnlockVideoUploadsCheck';

const EXTENSION_BLACKLIST = ['.exe', '.sh', '.svg'];

const getFileExtension = file => file.name.split('.').pop().replace('/[^a-zA-Z0-9]/', '');

const shouldRejectFile = file => EXTENSION_BLACKLIST.some(ext => file.name.endsWith(ext));

interface Props {
  deviation?: PapiDeviation;
  onSuccess?: (attachment: PapiAttachmentFile, deviation: PapiDeviation | undefined) => void;
}
export function useAttachmentManager({ deviation, onSuccess }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMultiVideo = useFlag('multi_video');
  const additionalAttachmentType = getAdditionalAttachmentType(isMultiVideo);
  const additionalAcceptedMimeTypes = getAdditionalAttachmentAcceptedMimeTypes(isMultiVideo);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<number | undefined>();
  const hasCoreMembership = useSelector(getCurrentUserHasCore);
  const maxAttachmentsAllowed = useMaxAttachmentsAllowed();
  const isLocked = useSelector(state =>
    getIsAttachmentDeviationLocked(state, deviation?.deviationId || -1)
  );

  const [attachments, setAttachments] = useState<PapiAttachmentFile[]>(
    deviation?.extended?.additionalMedia || []
  );

  const canUploadMore = attachments.length < maxAttachmentsAllowed;

  useEffect(() => setAttachments(deviation?.extended?.additionalMedia || []), [deviation]);

  const handleUploadSuccess: UploadAttachmentSuccessCallback = useCallback(
    ({ fileId, attachment, deviation: updatedDeviation }) => {
      if (attachment) {
        setAttachments([...attachments, attachment]);
        onSuccess?.(attachment, updatedDeviation);
      }
      setSelectedAttachmentId(fileId);
    },
    [attachments, onSuccess, setAttachments]
  );

  const handleUploadFailure = useCallback(
    ({ uploadId }, error) => {
      dispatch(
        addErrorNotification(
          error.message || t('deviationSubmit.validation.error.attachments.uploadFailed')
        )
      );
    },
    [t, dispatch]
  );

  const showAdditionalImagesPopup = useCallback(
    () =>
      dispatch(
        pushModal(ModalType.DEVIATION_SUBMIT_ADDITIONAL_IMAGES, {
          params: { deviation },
        })
      ),
    [dispatch, deviation]
  );

  const showAdditionalImagesUpsellPopup = useCallback(
    () =>
      dispatch(
        pushModal(ModalType.DEVIATION_SUBMIT_ADDITIONAL_IMAGES_UPSELL, {
          params: {},
        })
      ),
    [dispatch]
  );

  const showMaxLimitReachedPopup = useCallback(
    () =>
      dispatch(
        pushGlobalConfirmationModal({
          titleTransKey: 'deviationSubmit.modals.maximumAttachmentsReached.title',
          textTransKey: 'deviationSubmit.modals.maximumAttachmentsReached.text',
          confirmBtnType: 'default',
          confirmBtnLabelTransKey: 'common.gotIt',
          withCancelButton: false,
        })
      ),
    [dispatch]
  );

  const showUpsell = useCallback(
    () => (hasCoreMembership ? showMaxLimitReachedPopup() : showAdditionalImagesUpsellPopup()),
    [hasCoreMembership, showMaxLimitReachedPopup, showAdditionalImagesUpsellPopup]
  );

  const unlockVideoAttachmentCheck = useUnlockVideoUploadsCheck();

  const videoAttachmentValidator = useCallback(
    (file: File) => unlockVideoAttachmentCheck.validator(file),
    [unlockVideoAttachmentCheck.validator]
  );

  const handleAttachmentDropRejected = useCallback(
    (rejected: FileRejection[]) => {
      if (!isMultiVideo) {
        return;
      }
      unlockVideoAttachmentCheck.onDropRejected(rejected);
    },
    [isMultiVideo, unlockVideoAttachmentCheck]
  );

  const handleDrop = useCallback(
    (files, e): boolean => {
      const rejectedFile = files.find(shouldRejectFile);
      if (rejectedFile) {
        dispatch(
          addErrorNotification(
            t('deviationSubmit.validation.error.attachments.unsupported', {
              extension: getFileExtension(rejectedFile),
            })
          )
        );
        /* reject files */
        return false;
      }
      if (attachments.length + files.length > maxAttachmentsAllowed) {
        showUpsell();
        return false;
      }

      /* accept files */
      return true;
    },
    [t, dispatch, attachments, maxAttachmentsAllowed, showUpsell]
  );

  const {
    input,
    showFilePicker,
    activeUploads,
    cancelUpload,
    getDropzoneRootProps,
    isDragging,
    isDragAccept,
    isDragReject,
  } = useFileUpload({
    id: 'attachment-manager',
    uploadType: UploadType.ATTACHMENT,
    uploadParams: {
      parentDeviationId: deviation?.deviationId,
      attachmentType: additionalAttachmentType,
    },
    acceptedMimeTypes: additionalAcceptedMimeTypes,
    onSuccess: handleUploadSuccess,
    onFailure: handleUploadFailure,
    onDrop: handleDrop,
    validator: isMultiVideo ? videoAttachmentValidator : undefined,
    onDropRejected: handleAttachmentDropRejected,
    allowClickOnRoot: false,
    allowMultiple: true,
    allowDragAndDrop: true,
  });

  const repositionAttachment = useCallback(
    (fileId: number, newPosition: number) => {
      const attachmentIndex = attachments.findIndex(attachment => attachment.fileId === fileId);
      if (attachmentIndex === -1) {
        return;
      }
      const newAttachments = [...attachments];
      const [attachment] = newAttachments.splice(attachmentIndex, 1);
      newAttachments.splice(newPosition, 0, attachment);
      setAttachments(newAttachments);
    },
    [attachments]
  );

  const removeAttachmentByFileId = useCallback(
    (fileId: number, callback?: () => void) => {
      if (!fileId || !deviation?.deviationId) {
        return;
      }
      dispatch(
        removeAttachment(fileId, deviation?.deviationId, additionalAttachmentType, () => {
          setAttachments(attachments.filter(item => item.fileId !== fileId));
          if (selectedAttachmentId === fileId) {
            // select previous
            const currAttachmentIndex = attachments.findIndex(
              attachment => attachment.fileId === fileId
            );
            if (currAttachmentIndex !== -1) {
              // select next attachment
              const nextAttachmentIndex = currAttachmentIndex + 1;
              if (nextAttachmentIndex < attachments.length) {
                setSelectedAttachmentId(attachments[nextAttachmentIndex].fileId);
              } else {
                // in case of last item, select the previous attachment
                const prevAttachmentIndex = currAttachmentIndex - 1;
                if (prevAttachmentIndex >= 0) {
                  setSelectedAttachmentId(attachments[prevAttachmentIndex].fileId);
                }
              }
            }
          }
          callback?.();
        })
      );
    },
    [dispatch, selectedAttachmentId, attachments, deviation, additionalAttachmentType]
  );

  // For multiple uploads, take all active uploads and calculate the progress
  const totalActiveUploads = activeUploads?.length || 0;
  const totalActiveProgress = [...(activeUploads || [])].reduce(
    (result, item) => result + (item?.progress || 0),
    0
  );
  const isUploading = totalActiveUploads > 0;
  const activeUploadsProgress = isUploading ? totalActiveProgress / totalActiveUploads : 0;

  return {
    // useFileUpload hook props
    input,
    showFilePicker: canUploadMore ? showFilePicker : showUpsell,
    isUploading,
    activeUploads,
    activeUploadsProgress,
    cancelUpload,
    getDropzoneRootProps,
    isDragging,
    isDragAccept,
    isDragReject,
    isLocked,
    selectedAttachmentId,
    selectAttachmentId: setSelectedAttachmentId,

    // Attachments management
    canUploadMore,
    attachments,
    maxAttachmentsAllowed,
    uploadAttachment,
    repositionAttachment,
    removeAttachment: removeAttachmentByFileId,
    showAdditionalImagesPopup: canUploadMore ? showAdditionalImagesPopup : showUpsell,

    unlockVideoAttachmentsModal: isMultiVideo ? unlockVideoAttachmentCheck.modal : null,
  };
}

export default useAttachmentManager;
